import { Component, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import 'moment/locale/fr';

import { environment } from '../../../environments/environment';
import { MpsDateFormatService } from '../../services/mps-date-format.service';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsFooterComponent } from '../mps-footer/mps-footer.component';
import { MpsHeaderComponent } from '../mps-header/mps-header.component';
import { MpsLoaderComponent } from '../mps-loader/mps-loader.component';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-root',
  templateUrl: './mps-app.component.html',
  styleUrl: './mps-app.component.scss',
  imports: [
    MpsLoaderComponent,
    MpsHeaderComponent,
    MpsFooterComponent,
    MpsNavigationComponent,
    TranslateModule,
    MatSidenavModule,
    RouterOutlet,
  ],
})
export class MpsAppComponent {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(MpsUserService);
  private readonly user = signal(this.userService.getUserSubject().getValue());
  private readonly dateAdapter = inject(DateAdapter<Date>);
  private readonly dateFormatService = inject(MpsDateFormatService);

  public constructor() {
    this.dateFormatService.setLocale(environment.defaultLocale);
    this.dateAdapter.setLocale(environment.defaultLocale);
    this.registerSvgIcon();
    this.initTranslationService();
  }

  private initTranslationService() {
    this.translateService.setDefaultLang(environment.defaultLocale);
    this.translateService.addLangs(environment.allowedLanguage);

    // Set a default language if there's no language set
    if (!this.translateService.currentLang) {
      const userLang = this.user()?.lang;
      this.translateService.use(
        userLang && this.translateService.getLangs().includes(userLang)
          ? userLang
          : (localStorage.getItem('lang') as string) || environment.defaultLocale
      );
    }

    this.translateService.onLangChange.subscribe((event) => {
      this.dateFormatService.setLocale(event.lang);
      this.dateAdapter.setLocale(event.lang);
      localStorage.setItem('lang', event.lang);
    });
  }

  private registerSvgIcon() {
    const icons = [...environment.mpsIcons, ...environment.appIcons];

    icons.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        `total-${icon}`,
        this.domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/icons/${icon}.svg`)
      );
    });
  }
}
